import React, { useState, useEffect } from "react";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaGoogle,
  FaYoutube,
  FaArrowUp,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaLightbulb,
  FaNewspaper,
  FaKey,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import InquiryForm from "./Footer/InquiryForm";
import WhatsAppIcon from "./Footer/WhatsAppIcon";
import { getApiUrl } from "../utils/api";
import "./design/Footer.css";

function Footer() {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [addressLink, setAddressLink] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [disclaimer, setDisclaimer] = useState(null);
  const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => setShowScrollButton(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch address information
  useEffect(() => {
    fetch(getApiUrl("get_addresses.php"))
      .then((res) => res.json())
      .then((data) => {
        console.log("API Response:", data);
        if (data.status && data.data.length > 0) {
          setAddressLink(data.data[0].address_link);
          setContactNumber(data.data[0].contact_number);
          setFullAddress(data.data[0].full_address);
          setEmailAddress(data.data[0].email_id);
        }
      })
      .catch((error) => console.error("API Error:", error));
  }, []);

  // Fetch disclaimer from API
  useEffect(() => {
    const fetchDisclaimer = async () => {
      setLoadingDisclaimer(true);
      try {
        const response = await fetch(getApiUrl("get_disclaimer.php?active=1"));
        const data = await response.json();
        
        if (data.status && data.data) {
          setDisclaimer(data.data);
        }
      } catch (error) {
        console.error("Error fetching disclaimer:", error);
      } finally {
        setLoadingDisclaimer(false);
      }
    };
    
    fetchDisclaimer();
  }, []);

  // Function to render disclaimer content
  const renderDisclaimerContent = () => {
    if (loadingDisclaimer) {
      return (
        <div className="disclaimer-loading">
          <div className="disclaimer-spinner"></div>
        </div>
      );
    }

    if (disclaimer && disclaimer.content) {
      return (
        <div 
          className="disclaimer-content-api"
          dangerouslySetInnerHTML={{ __html: disclaimer.content }}
        />
      );
    }

    // Fallback content if API fails
    return (
      <>
        <h4>Disclaimer</h4>
        <p>
          The content on Franmax India is for general informational purposes
          only. We do not provide professional advice. Before making financial
          or business decisions, please consult a certified expert.
        </p>
        <p>
          <strong>Use of this website</strong> is at your own risk. All brand
          names, trademarks, and logos belong to their respective owners. Data
          shown here may be collected from public sources and might not always
          reflect the latest updates.
        </p>
        <p>
          Franmax India does not claim ownership of third-party listings. Use
          your discretion before engaging with businesses. If you believe any
          content needs correction, contact us at{" "}
          <a href="mailto:info@franmaxindia.com" className="highlight-text">
            info@franmaxindia.com
          </a>
          .
        </p>
        <p>
          Please also read our{" "}
          <Link to="/terms-of-use" className="highlight-text">
            Terms of Use
          </Link>{" "}
          for complete details.
        </p>
      </>
    );
  };

  return (
    <>
      {/* Disclaimer Section - Now fetching from API */}
      <div className="premium-disclaimer">
        <div className="disclaimer-card">
          {renderDisclaimerContent()}
        </div>
      </div>

      {/* Inquiry Form */}
      <div className="premium-inquiry">
        <InquiryForm />
      </div>

      {/* Footer Section */}
      <footer className="premium-footer">
        <div className="footer-container">
          {/* About Card */}
          <div className="footer-card">
            <h4>About Us</h4>
            <p>
              <Link to="/about" className="about-us-link">
                Franmax India is a leading platform connecting entrepreneurs with the best franchise opportunities across India.
              </Link>
            </p>
            <p>
              &copy; 2020–{new Date().getFullYear()} Franmax India (P) Ltd. All
              rights reserved.
            </p>
            <p>
              <FaEnvelope className="footer-icon" />{" "}
              <a
                href={`mailto:${emailAddress}`}
                className="footer-link highlight-text"
                style={{ color: "#060644" }}
              >
                {emailAddress}
              </a>
            </p>
          </div>

          {/* Contact Card */}
          <div className="footer-card contact-card">
            <h4>Get in Touch</h4>
            <p className="contact-intro">
              We're here to help! Reach out for inquiries or support.
            </p>
            <ul className="contact-details-list">
              <li>
                <FaMapMarkerAlt className="footer-icon" />
                <a
                  href={addressLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="address-text"
                >
                  {fullAddress}
                </a>
              </li>
              <li>
                <FaPhoneAlt className="footer-icon" />
                <a href={`tel:${contactNumber}`} className="footer-link">
                  {contactNumber}
                </a>
              </li>
            </ul>
          </div>

          {/* Social Media Card */}
          <div className="footer-card">
            <h4>Follow Us</h4>
            <div className="social-benefits">
              <p>Follow our journey on social media for:</p>
              <ul>
                <li><FaLightbulb className="benefit-icon" /> Expert Insights</li>
                <li><FaNewspaper className="benefit-icon" /> Industry News</li>
                <li><FaKey className="benefit-icon" /> Exclusive Opportunities</li>
              </ul>
            </div>
            <div className="social-icons">
              <a href="https://www.instagram.com/franmax_india" target="_blank" rel="noopener noreferrer" className="social-instagram">
                <FaInstagram />
              </a>
              <a href="https://www.facebook.com/@franmaxindia" target="_blank" rel="noopener noreferrer" className="social-facebook">
                <FaFacebookF />
              </a>
              <a href="https://g.co/kgs/zeVko4Q" target="_blank" rel="noopener noreferrer" className="social-google">
                <FaGoogle />
              </a>
              <a href="https://www.linkedin.com/company/franmaxindia/" target="_blank" rel="noopener noreferrer" className="social-linkedin">
                <FaLinkedinIn />
              </a>
              <a href="https://www.youtube.com/@Franmaxindia" target="_blank" rel="noopener noreferrer" className="social-youtube">
                <FaYoutube />
              </a>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="footer-quick-links-row">
          <Link to="/about" className="footer-link">About Us</Link>
          <Link to="/terms-of-use" className="footer-link">Terms of Use</Link>
          <Link to="/cookies-policy" className="footer-link">Cookies & Policy</Link>
          <Link to="/privacy-policy" className="footer-link">Privacy & Policy</Link>
          <Link to="/payment-terms" className="footer-link">Payment Terms</Link>
          <Link to="/faqs" className="footer-link">FAQS</Link>
          <Link to="/our-team" className="footer-link">Team</Link>
          <Link to="/contact-us" className="footer-link">Contact</Link>
        </div>

        <WhatsAppIcon />
      </footer>

      {/* Scroll-to-Top Button */}
      {showScrollButton && (
        <button onClick={scrollToTop} className="back-to-top-btn">
          <FaArrowUp /> Top
        </button>
      )}
    </>
  );
}

export default Footer;